import type { CSSProperties } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { operationalStyles, operationalTheme } from "./operationalStyles";

type OperationalPagerProps = {
  variant?: "default" | "numbered";
  page?: number;
  pageCount?: number;
  totalItems: number;
  pageSize?: number;
  limit?: number;
  offset?: number;
  isFetching?: boolean;
  onPageChange: (page: number) => void;
  ariaLabel?: string;
  itemLabel?: string;
  itemLabelPlural?: string;
  compact?: boolean;
  className?: string;
};

type NumberedPageToken = number | "ellipsis";

export function getNumberedPageTokens(
  page: number,
  pageCount: number,
): NumberedPageToken[] {
  const safePageCount = Math.max(0, Math.floor(pageCount));
  if (safePageCount <= 5) {
    return Array.from({ length: safePageCount }, (_, index) => index);
  }

  const currentPage = Math.min(
    Math.max(Math.floor(page), 0),
    safePageCount - 1,
  );
  const visiblePages = new Set([
    0,
    safePageCount - 1,
    currentPage - 1,
    currentPage,
    currentPage + 1,
  ]);

  if (currentPage === 0) visiblePages.add(2);
  if (currentPage === safePageCount - 1) visiblePages.add(safePageCount - 3);

  const sortedPages = Array.from(visiblePages)
    .filter((visiblePage) => visiblePage >= 0 && visiblePage < safePageCount)
    .sort((left, right) => left - right);

  return sortedPages.flatMap<NumberedPageToken>((visiblePage, index) => {
    const previousPage = sortedPages[index - 1];
    if (previousPage !== undefined && visiblePage - previousPage > 1) {
      return ["ellipsis", visiblePage];
    }
    return [visiblePage];
  });
}

const footerStyle: CSSProperties = {
  minHeight: "3.75rem",
  padding: "0.75rem 1rem",
  boxSizing: "border-box",
  borderTop: `1px solid ${operationalTheme.border}`,
  borderRadius: 0,
  background: "transparent",
  display: "grid",
  gridTemplateColumns: "1fr auto 1fr",
  alignItems: "center",
  gap: "0.75rem",
};

export function OperationalPager({
  variant = "default",
  page = 0,
  pageCount,
  totalItems,
  pageSize = 20,
  limit,
  offset,
  isFetching = false,
  onPageChange,
  ariaLabel = "Paginação",
  itemLabel = "notificação",
  itemLabelPlural,
  compact = false,
  className,
}: OperationalPagerProps) {
  const safeLimit = Math.max(1, Math.floor(limit ?? pageSize));
  const derivedPageCount = Math.max(1, Math.ceil(totalItems / safeLimit));
  const safePageCount = Math.max(1, Math.floor(pageCount ?? derivedPageCount));
  const clampedPage = Math.min(Math.max(page, 0), safePageCount - 1);
  const maxOffset = Math.max(
    0,
    Math.floor((totalItems - 1) / safeLimit) * safeLimit,
  );
  const safeOffset =
    totalItems === 0
      ? 0
      : Math.min(Math.max(offset ?? clampedPage * safeLimit, 0), maxOffset);
  const currentPage = Math.floor(safeOffset / safeLimit);
  const canGoBack = currentPage > 0;
  const canGoForward = safeOffset + safeLimit < totalItems;
  const pluralLabel =
    itemLabelPlural ??
    (itemLabel === "notificação" ? "notificações" : itemLabel);
  const totalLabel =
    totalItems === 1 ? `1 ${itemLabel}` : `${totalItems} ${pluralLabel}`;
  const rangeLabel =
    totalItems === 0
      ? "0–0 de 0"
      : `${safeOffset + 1}–${Math.min(safeOffset + safeLimit, totalItems)} de ${totalItems}`;

  if (variant === "numbered") {
    if (safePageCount <= 1) return null;

    const numberedCanGoBack = currentPage > 0;
    const numberedCanGoForward = currentPage < safePageCount - 1;
    const pageTokens = getNumberedPageTokens(currentPage, safePageCount);
    const numberedButtonStyle: CSSProperties = {
      minWidth: "2.75rem",
      height: "2.25rem",
      border: 0,
      borderRadius: 0,
      background: "transparent",
      padding: "0 0.125rem",
      fontFamily: "'DM Mono', monospace",
      fontSize: "0.75rem",
      fontWeight: 600,
    };

    return (
      <>
        <style>{`
          .operational-pager--numbered {
            display: flex !important;
            justify-content: space-between;
            background: transparent !important;
          }
          .operational-pager--numbered .pager-controls {
            min-width: 0;
            max-width: 100%;
            gap: 1rem !important;
          }
          .operational-pager--numbered .pager-ellipsis {
            width: 1rem;
            flex: 0 0 auto;
            color: ${operationalTheme.muted};
            text-align: center;
          }
          .operational-pager--numbered .pager-action {
            min-height: 2.75rem;
            border: 0;
            background: transparent;
            padding: 0;
            font-size: 0.8rem;
            font-weight: 650;
          }
          .operational-pager--numbered .pager-page[aria-current="page"] {
            box-shadow: inset 0 -2px 0 ${operationalTheme.accent};
          }
          .operational-pager--numbered[data-compact="true"] {
            grid-template-columns: auto !important;
            justify-content: center;
            padding: 0.75rem 0;
          }
          .operational-pager--numbered[data-compact="true"] .pager-controls {
            grid-column: 1;
          }
          @media (max-width: 768px) {
            .operational-pager--numbered .pager-controls .pager-page {
              height: 2.75rem !important;
            }
          }
          @media (max-width: 520px) {
            .operational-pager--numbered {
              padding: 0.65rem 0.75rem !important;
              flex-wrap: wrap;
              justify-content: center;
              gap: 0.25rem !important;
            }
            .operational-pager--numbered .pager-range {
              width: 100%;
              min-width: 0 !important;
              text-align: center;
            }
            .operational-pager--numbered .pager-controls {
              gap: 0.25rem !important;
            }
          }
          @media (max-width: 640px) {
            .operational-pager--numbered {
              min-height: 3.75rem !important;
              align-items: center;
            }
            .operational-pager--numbered .pager-range {
              font-size: 0.7rem !important;
            }
          }
          @media (max-width: 640px) {
            .operational-pager--numbered .pager-page[data-page-role="boundary"],
            .operational-pager--numbered .pager-ellipsis {
              display: none;
            }
          }
        `}</style>
        <nav
          aria-label={ariaLabel}
          aria-busy={isFetching || undefined}
          className={[
            "operational-pager",
            "operational-pager--numbered",
            className,
          ]
            .filter(Boolean)
            .join(" ")}
          data-compact={compact ? "true" : undefined}
          data-variant="numbered"
          style={footerStyle}
        >
          {!compact && (
            <span
              className="pager-range"
              style={{
                minWidth: "7.5rem",
                color: operationalTheme.muted,
                fontSize: "0.78rem",
                whiteSpace: "nowrap",
              }}
            >
              {rangeLabel}
            </span>
          )}

          <div
            className="pager-controls"
            style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}
          >
            <button
              type="button"
              className="pager-action"
              aria-label="Anterior"
              disabled={isFetching || !numberedCanGoBack}
              onClick={() => {
                if (!isFetching && numberedCanGoBack) {
                  onPageChange(currentPage - 1);
                }
              }}
              style={{
                color: numberedCanGoBack
                  ? operationalTheme.text
                  : operationalTheme.muted,
                cursor:
                  !isFetching && numberedCanGoBack ? "pointer" : "not-allowed",
                opacity: numberedCanGoBack ? 1 : 0.55,
              }}
            >
              Anterior
            </button>

            {pageTokens.map((token, index) => {
              if (token === "ellipsis") {
                return (
                  <span
                    key={`ellipsis-${index}`}
                    className="pager-ellipsis"
                    data-page-role="ellipsis"
                    aria-hidden="true"
                  >
                    …
                  </span>
                );
              }

              const pageRole =
                token === currentPage
                  ? "current"
                  : Math.abs(token - currentPage) === 1
                    ? "neighbor"
                    : token === 0 || token === safePageCount - 1
                      ? "boundary"
                      : "context";

              return (
                <button
                  key={token}
                  type="button"
                  className="pager-page"
                  data-page-role={pageRole}
                  aria-label={`Página ${token + 1}`}
                  aria-current={token === currentPage ? "page" : undefined}
                  disabled={isFetching}
                  onClick={() => {
                    if (!isFetching) onPageChange(token);
                  }}
                  style={{
                    ...numberedButtonStyle,
                    color:
                      token === currentPage
                        ? operationalTheme.text
                        : operationalTheme.muted,
                    cursor: isFetching ? "not-allowed" : "pointer",
                  }}
                >
                  {token + 1}
                </button>
              );
            })}

            <button
              type="button"
              className="pager-action"
              aria-label="Próximo"
              disabled={isFetching || !numberedCanGoForward}
              onClick={() => {
                if (!isFetching && numberedCanGoForward) {
                  onPageChange(currentPage + 1);
                }
              }}
              style={{
                color: numberedCanGoForward
                  ? operationalTheme.text
                  : operationalTheme.muted,
                cursor:
                  !isFetching && numberedCanGoForward
                    ? "pointer"
                    : "not-allowed",
                opacity: numberedCanGoForward ? 1 : 0.55,
              }}
            >
              Próximo
            </button>
          </div>
        </nav>
      </>
    );
  }

  return (
    <>
      <style>{`
        @media (max-width: 768px) {
          .operational-pager[data-variant="default"] .pager-controls button {
            width: 2.75rem !important;
            height: 2.75rem !important;
          }
        }
        @media (max-width: 520px) {
          .operational-pager[data-variant="default"] {
            min-height: 5.5rem !important;
            padding: 0.65rem 0.25rem !important;
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important;
            grid-template-rows: auto auto;
            gap: 0.4rem !important;
          }
          .operational-pager[data-variant="default"] .pager-controls {
            grid-column: 1 / -1;
            grid-row: 1;
            justify-self: center;
          }
          .operational-pager[data-variant="default"] .pager-count {
            grid-column: 1;
            grid-row: 2;
            min-width: 0 !important;
            padding-left: 0.25rem;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .operational-pager[data-variant="default"] .pager-range {
            grid-column: 2;
            grid-row: 2;
            min-width: 0 !important;
            padding-right: 0.25rem;
          }
          .operational-pager[data-variant="default"][data-compact="true"] {
            min-height: 3.75rem !important;
            padding: 0.65rem 0.25rem !important;
            grid-template-columns: auto !important;
            grid-template-rows: auto;
          }
          .operational-pager[data-variant="default"][data-compact="true"] .pager-controls {
            grid-column: 1;
            grid-row: 1;
          }
        }
        .operational-pager[data-variant="default"][data-compact="true"] {
          grid-template-columns: auto;
          justify-content: center;
          padding: 0.75rem 0;
        }
      `}</style>
      <nav
        aria-label={ariaLabel}
        className={["operational-pager", className].filter(Boolean).join(" ")}
        data-compact={compact ? "true" : undefined}
        data-variant="default"
        style={footerStyle}
      >
        {!compact && (
          <span
            className="pager-count"
            style={{
              minWidth: "7.5rem",
              color: operationalTheme.muted,
              fontSize: "0.78rem",
              whiteSpace: "nowrap",
            }}
          >
            {isFetching ? "Atualizando..." : totalLabel}
          </span>
        )}

        <div
          className="pager-controls"
          style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}
        >
          <button
            type="button"
            aria-label="Página anterior"
            title="Página anterior"
            disabled={!canGoBack}
            onClick={() => {
              if (canGoBack) onPageChange(currentPage - 1);
            }}
            style={{
              ...operationalStyles.controlButton,
              color: canGoBack
                ? operationalTheme.text
                : operationalTheme.border,
              cursor: canGoBack ? "pointer" : "not-allowed",
            }}
          >
            <ChevronLeft size={16} aria-hidden="true" />
          </button>
          <span
            aria-live="polite"
            style={{
              minWidth: "6.25rem",
              textAlign: "center",
              fontFamily: "'DM Mono', monospace",
              color: operationalTheme.muted,
              fontSize: "0.8rem",
            }}
          >
            Página {currentPage + 1} de{" "}
            {Math.max(safePageCount, currentPage + 1)}
          </span>
          <button
            type="button"
            aria-label="Próxima página"
            title="Próxima página"
            disabled={!canGoForward}
            onClick={() => {
              if (canGoForward) onPageChange(currentPage + 1);
            }}
            style={{
              ...operationalStyles.controlButton,
              color: canGoForward
                ? operationalTheme.text
                : operationalTheme.border,
              cursor: canGoForward ? "pointer" : "not-allowed",
            }}
          >
            <ChevronRight size={16} aria-hidden="true" />
          </button>
        </div>

        {!compact && (
          <span
            className="pager-range"
            aria-hidden="true"
            style={{
              minWidth: "7.5rem",
              textAlign: "right",
              color: operationalTheme.muted,
              fontSize: "0.78rem",
              whiteSpace: "nowrap",
            }}
          >
            {rangeLabel}
          </span>
        )}
      </nav>
    </>
  );
}
